// src/pages/chat/dock/dock.tsx
/** @jsxImportSource @emotion/react */
import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  DockShell,
  HelperText,
  DockWrapper,
  DockContent,
  DockItem,
  DockIcon,
  DockLabel,
} from './dock.styled';
import { useDock } from './dock.hooks';
import { Type } from './type/type';
import { NoteModal } from './NoteModal';
import { AttachmentModal } from './AttachmentModal';
import { useBackHandler } from '../../../platform/use-back-handler';

export type DockHandle = {
  closeType: () => void;
  closeTypeImmediate: () => void;
};

type DockProps = {
  onStartRecording: () => void;
  onEnterFullscreen: () => void;
  onSubmitMessage: (text: string, files?: File[]) => Promise<void> | void;
  onSaveNote: (content: string) => Promise<void>;
  onAttachFile: () => void;
  onAttachPhoto: () => void;
  onOpenConversation?: () => void;

  /** When true, HelperText should unmount/disappear */
  showConversation?: boolean;

  /** Disable send actions (e.g. while a submission is in-flight) */
  disabled?: boolean;
  /** Upload progress percentage (0-100) for visual feedback */
  uploadProgress?: number | null;
  /** Surface error messages (e.g. unsupported file types) */
  onError?: (msg: string) => void;
};

export const Dock = React.forwardRef<DockHandle, DockProps>(({
  onStartRecording,
  onEnterFullscreen,
  onSubmitMessage,
  onSaveNote,
  onAttachFile,
  onAttachPhoto,
  onOpenConversation,
  showConversation = false,
  disabled = false,
  uploadProgress,
  onError,
}, ref) => {
  const { mode, isExiting, onExitEnd, openType, closeType, closeTypeImmediate } = useDock();
  const typeRootRef = React.useRef<HTMLDivElement>(null);

  // Click-outside to close Type panel — replaces the full-screen backdrop
  // that used to block scroll events on the conversation behind it.
  // Uses pointerdown+pointerup to distinguish taps from scroll gestures.
  const outsideDownRef = React.useRef<{ x: number; y: number } | null>(null);
  React.useEffect(() => {
    if (mode !== 'type') return;
    const onDown = (e: PointerEvent) => {
      if (typeRootRef.current && !typeRootRef.current.contains(e.target as Node)) {
        outsideDownRef.current = { x: e.clientX, y: e.clientY };
      } else {
        outsideDownRef.current = null;
      }
    };
    const onUp = (e: PointerEvent) => {
      const start = outsideDownRef.current;
      outsideDownRef.current = null;
      if (!start) return;
      // Only act if it was a tap (minimal movement), not a scroll gesture
      const dx = Math.abs(e.clientX - start.x);
      const dy = Math.abs(e.clientY - start.y);
      if (dx < 10 && dy < 10 && !disabled) {
        const focusedEl = document.activeElement;
        if (typeRootRef.current && focusedEl && typeRootRef.current.contains(focusedEl)) {
          // First tap: hide keyboard by blurring the focused input
          (focusedEl as HTMLElement).blur();
        } else {
          // Second tap (keyboard already hidden): close the modal
          closeType();
        }
      }
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('pointerup', onUp, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('pointerup', onUp, true);
    };
  }, [mode, closeType, disabled]);

  React.useImperativeHandle(ref, () => ({ closeType, closeTypeImmediate }), [closeType, closeTypeImmediate]);
  const [showNoteModal, setShowNoteModal] = React.useState(false);
  const [showAttachmentModal, setShowAttachmentModal] = React.useState(false);

  const closeNoteModal = React.useCallback(() => setShowNoteModal(false), []);
  const closeAttachmentModal = React.useCallback(() => setShowAttachmentModal(false), []);
  useBackHandler('noteModal', showNoteModal, closeNoteModal);
  useBackHandler('attachmentModal', showAttachmentModal, closeAttachmentModal);

  const handleNoteClickLocal = React.useCallback(() => {
    if (disabled) return;
    setShowNoteModal((prev) => !prev);
    setShowAttachmentModal(false);
  }, [disabled]);

  const handleAttachClickLocal = React.useCallback(() => {
    if (disabled) return;
    setShowAttachmentModal((prev) => !prev);
    setShowNoteModal(false);
  }, [disabled]);

  const handleTypeClick = React.useCallback(() => {
    if (disabled) return;
    openType();
    setShowNoteModal(false);
    setShowAttachmentModal(false);
    onOpenConversation?.();
  }, [openType, onOpenConversation, disabled]);

  const handleMicClick = React.useCallback(() => {
    if (disabled) return;
    onStartRecording();
    setShowNoteModal(false);
    setShowAttachmentModal(false);
  }, [onStartRecording, disabled]);

  const swipeRef = React.useRef<{
    active: boolean;
    startX: number;
    startY: number;
    triggered: boolean;
    pointerId: number | null;
  }>({
    active: false,
    startX: 0,
    startY: 0,
    triggered: false,
    pointerId: null,
  });

  const onPointerDown = React.useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'mouse') return;
    if (disabled) return;

    swipeRef.current.active = true;
    swipeRef.current.triggered = false;
    swipeRef.current.startX = e.clientX;
    swipeRef.current.startY = e.clientY;
    swipeRef.current.pointerId = e.pointerId;

    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  }, []);

  const onPointerMove = React.useCallback(
    (e: React.PointerEvent) => {
      if (!swipeRef.current.active || swipeRef.current.triggered) return;
      if (e.pointerType === 'mouse') return;

      const dx = e.clientX - swipeRef.current.startX;
      const dy = e.clientY - swipeRef.current.startY;

      if (Math.abs(dy) < 10) return;
      if (Math.abs(dy) < Math.abs(dx)) return;

      e.preventDefault();

      const SWIPE_UP_PX = 70;

      if (dy < -SWIPE_UP_PX) {
        swipeRef.current.triggered = true;
        onEnterFullscreen();
      }
    },
    [onEnterFullscreen]
  );

  const onPointerUpOrCancel = React.useCallback((e: React.PointerEvent) => {
    if (!swipeRef.current.active) return;

    const pid = swipeRef.current.pointerId;
    swipeRef.current.active = false;
    swipeRef.current.pointerId = null;

    try {
      if (pid != null) (e.currentTarget as HTMLElement).releasePointerCapture(pid);
    } catch {
      // ignore
    }
  }, []);

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
      }}
    >
      {/* ── Idle dock (buttons) ── */}
      {mode === 'dock' && (
        <DockShell
          initial={{ y: '100%' }}
          animate={isExiting ? { y: '100%' } : { y: 0 }}
          transition={{ type: 'spring', stiffness: 420, damping: 34 }}
          onAnimationComplete={() => { if (isExiting) onExitEnd(); }}
        >
          <AnimatePresence initial={false}>
            {!showConversation && (
              <motion.div
                key="dock-helper"
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 28, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 420, damping: 34 }}
              >
                <HelperText>Tap the M to speak, or write and attach below.</HelperText>
              </motion.div>
            )}
          </AnimatePresence>

          <DockWrapper
            style={{ touchAction: 'pan-x' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUpOrCancel}
            onPointerCancel={onPointerUpOrCancel}
          >
            <DockContent>
              <DockItem type="button" onClick={handleNoteClickLocal}>
                <DockIcon src="/note.svg" alt="Note" isActive={showNoteModal} />
                <DockLabel>Note</DockLabel>
              </DockItem>

              <DockItem type="button" onClick={handleAttachClickLocal}>
                <DockIcon src="/attach.svg" alt="Attach" isActive={showAttachmentModal} />
                <DockLabel>Attach</DockLabel>
              </DockItem>

              <DockItem type="button" onClick={handleTypeClick}>
                <DockIcon src="/type.svg" alt="Type" />
                <DockLabel>Type</DockLabel>
              </DockItem>

              <DockItem type="button" onClick={handleMicClick}>
                <DockIcon
                  style={{ height: '60px', width: '60px' }}
                  src="/dock-record-button.svg"
                  alt="Record"
                />
              </DockItem>
            </DockContent>
          </DockWrapper>
        </DockShell>
      )}

      {/* ── Type dock ── */}
      {mode === 'type' && (
        <div ref={typeRootRef} style={{ width: '100%' }}>
          <Type
            onClose={closeType}
            onSubmitMessage={onSubmitMessage}
            onMicClick={() => {
              closeType();
              onStartRecording();
              setShowNoteModal(false);
              setShowAttachmentModal(false);
            }}
            disabled={disabled}
            uploadProgress={uploadProgress}
            onError={onError}
            isExiting={isExiting}
            onExitEnd={onExitEnd}
          />
        </div>
      )}

      <NoteModal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        onSaveNote={onSaveNote}
      />

      <AttachmentModal
        isOpen={showAttachmentModal}
        onClose={() => setShowAttachmentModal(false)}
        onAttachFile={onAttachFile}
        onAttachPhoto={onAttachPhoto}
      />
    </div>
  );
});
